<template>
  <div class="mb-3">
    <vx-card :title="chart_name"> {{ report_data }}
      <div class="vx-row" id="sales-revenue-chart-card">

        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">
          <label class="text-sm opacity-100 text-bold">Range</label>
          <v-select :clearable="false" placeholder="" v-model="range_obj"
                    :options="ranges"
                    label="label"
                    @input="onChangeRange()"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"></v-select>
        </div>


        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">
          <label class="text-sm opacity-100 text-bold">From/To</label>
          <flat-pickr :config="{ dateFormat: 'd/m/Y', mode: 'range' }" class="w-full" v-model="filters.range"
                      placeholder="Choose Date"/>
        </div>


        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">
          <label class="text-sm opacity-100 text-bold">X-axis Interval</label>
          <v-select :clearable="false" placeholder="" v-model="filters.interval"
                    :options="x_axis_intervals"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"></v-select>
        </div>

        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">
          <label class="text-sm opacity-100 text-bold">Country</label>
          <v-select :clearable="true" placeholder="" v-model="filters.country"
                    :options="countries"
                    label="name"
                    :reduce="option => option.name"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"></v-select>
        </div>

        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">
          <label class="text-sm opacity-100 text-bold">Customer</label>
          <v-select :clearable="true" placeholder="" v-model="filters.customer"
                    :options="customers"
                    label="company_name"
                    :reduce="option => option.id"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"></v-select>
        </div>

<!--        <div class="vx-col md:w-1/5 sm:w-1/2 w-full">-->
<!--          <label class="text-sm opacity-100 text-bold">Chart Type</label>-->
<!--          <v-select :clearable="false" placeholder="" v-model="type"-->
<!--                    :options="['line', 'area', 'bar', 'scatter', 'pie']"-->
<!--                    :dir="$vs.rtl ? 'rtl' : 'ltr'"></v-select>-->
<!--        </div>-->
      </div>

      <div class="vx-row mt-4 mb-16" >
        <div class="vx-col w-full ml-auto">
          <vs-button  class="float-right mr-0" size="small"
                     color="warning" type="filled" icon-pack="feather" @click.stop="onClickChartConfig()">Edit Chart
          </vs-button>
        </div>
      </div>

      <vue-apex-charts
        v-show="type !== 'pie'"
        ref="lineApexChart"
        :type="type"
        height="300"
        :options="line_chart_data.chart_options"
        :series="line_chart_data.series"/>
      <vue-apex-charts
        v-show="type === 'pie'"
        ref="pieApexChart"
        :type="type"
        height="300"
        :options="pie_chart_data.chart_options"
        :series="pie_chart_data.series"/>

    </vx-card>
    <chart-configs-side-bar @saveConfig="saveConfig"
                            ref="chartConfigsSideBar"/>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import VueApexCharts from 'vue-apexcharts'
import moment from "moment";

import {RepositoryFactory} from '@/repository/RepositoryFactory'
import {mapActions, mapGetters} from "vuex";
import chart_data from "@/constants/charts.js";
import ChartConfigsSideBar from "@/components/sidebars/ChartConfigsSideBar";
import ErrorMessages from "@/mixins/ErrorMessages";

const ReportingRepository = RepositoryFactory.get('reporting')

export default {
  name: "SalesRevenue",
  components: {
    VueApexCharts,
    'v-select': vSelect,
    flatPickr,
    ChartConfigsSideBar
  },
  props: {
    report_data: {
      default: {},
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      is_loaded_completed: false,
      type: 'line',
      chart_types: ['line', 'bar', 'area', 'scatter'],
      ranges: [],
      line_chart_data: {},
      pie_chart_data: {},
      chart_data,
      range_obj: {},
      filters: {
        interval: 'monthly',
        range: null,
        country: '',
        customer: '',
      },
      chart_name: 'Sales Revenue',
      series_data: [],
      labels: [],
    }
  },
  created() {
    Object.assign(this.line_chart_data, this.chart_data.line_chart_data)
    Object.assign(this.pie_chart_data, this.chart_data.pie_chart_data)
  },
  /**
   * fetch filters data for dropdowns
   * @returns {Promise<void>}
   */
  async mounted() {
    await this.fetchCountries()
    await this.fetchCustomers()
    await this.rangeGenerator()
  },

  /**
   * destroy apex chart instance from dom
   */
  beforeDestroy() {
    this.$refs.pieApexChart.destroy()
    this.$refs.lineApexChart.destroy()
  },

  methods: {
    ...mapActions({
      fetchCustomers: 'fetchCustomers',
      fetchCountries: 'fetchCountries',
    }),

    /**
     * open chart config edit side bar
     */
    onClickChartConfig() {
      this.$refs.chartConfigsSideBar.openSideBar(this.chart_name, this.line_chart_data.chart_options, this.type)
    },

    /**
     * save chart settings and re-render chart with options
     * @param type
     * @param config
     */
    saveConfig({ type, config }) {
      try {
        /**
         * chart series update and re-render
         */
        this.type = type
        this.line_chart_data.chart_options = config
        this.$refs.lineApexChart.updateOptions({
          xaxis: { title: {text: config.xaxis.title.text} },
          yaxis: [{title: {text: config.yaxis.title.text}, tickAmount: Number(config.yaxis.tickAmount)}],
          colors: config.colors,
          grid: config.grid,
          stroke: {curve: config.stroke.curve, width: config.stroke.width},
        }, true, true)

      } catch (e) {

      }
    },

    /**
     * date range pre-defined list generator [last 30 days, 60 days, 90 days and 365 days]
     * @returns {Promise<void>}
     */
    async rangeGenerator() {
      const last_30_days = moment().subtract(30, 'days').format('DD/MM/YYYY')
      const last_60_days = moment().subtract(60, 'days').format('DD/MM/YYYY')
      const last_90_days = moment().subtract(90, 'days').format('DD/MM/YYYY')
      const last_180_days = moment().subtract(180, 'days').format('DD/MM/YYYY')
      const last_365_days = moment().subtract(365, 'days').format('DD/MM/YYYY')
      const today = moment().format('DD/MM/YYYY')
      this.ranges = [
        {label: 'Last 30 Days', value: `${last_30_days} to ${today}`},
        {label: 'Last 60 Days', value: `${last_60_days} to ${today}`},
        {label: 'Last 90 Days', value: `${last_90_days} to ${today}`},
        {label: 'Last 180 Days', value: `${last_180_days} to ${today}`},
        {label: 'Last 365 Days', value: `${last_365_days} to ${today}`},
      ]
      this.range_obj = this.ranges[this.ranges.length - 1]
      this.filters.range = this.ranges[this.ranges.length - 1].value
    },

    /**
     * set the date range when click on range pre-defined list
     */
    async onChangeRange() {
      this.filters.range = this.range_obj.value
    },

    /**
     * fetching chart data
     * @returns {Promise<void>}
     */
    async getChartData() {
      await this.$vs.loading({
        container: '#sales-revenue-chart-card',
        scale: 0.45
      })
      try {
        let arr = this.filters.range.split(' to ')
        if (arr.length === 2) {
          const {data} = (await ReportingRepository.salesRevenueChartData(
            arr[0],
            arr[1],
            this.filters.interval,
            this.filters.country ? this.filters.country : '',
            this.filters.customer ? this.filters.customer : ''
          )).data
          await this.setChartLabelsAndValues(data)
        }
      } catch (e) {
        this.showErrorMessageWithTitle('Something went wrong', 'Please contact admin')
      }
      this.$vs.loading.close('#sales-revenue-chart-card > .con-vs-loading')
    },

    async setChartLabelsAndValues(data) {
      this.series_data = data.map((x) => Number(x.value))
      this.labels = data.map((x) => x.label)

      // set line chart data & series
      this.line_chart_data.series[0].name = 'Revenue'
      this.line_chart_data.series[0].data = this.series_data
      this.line_chart_data.chart_options.xaxis.categories = this.labels

      //set pie chart data & series
      this.pie_chart_data.series = this.series_data
      this.pie_chart_data.chart_options.labels = this.labels

      this.$refs.pieApexChart.updateOptions({
        series: [{data: this.series_data}],
        labels: this.labels
      });
      this.$refs.lineApexChart.updateOptions({
        series: [{data: this.series_data}],
        xaxis: {categories: this.labels},
      });
    },
  },
  computed: {
    ...mapGetters(['customers', 'countries']),

    /**
     * date range gap calculation and interval dropdown generate
     * @returns {string[]}
     */
    x_axis_intervals: function () {
      if (!this.filters.range) {
        return []
      }
      let arr = this.filters.range.split(' to ')
      let from = moment(arr[0], 'DD/MM/YYYY')
      let to = moment(arr[1], 'DD/MM/YYYY')
      if (arr.length !== 2) {
        return []
      }
      let gap = to.diff(from, 'days')
      if (gap < 8) {
        this.filters.interval = 'daily'
        return ['daily']
      } else if (gap < 30) {
        this.filters.interval = 'weekly'
        return ['weekly', 'daily']
      } else {
        return ['monthly', 'weekly', 'daily']
      }
    }
  },
  mixins: [ErrorMessages],
  watch: {
    'filters': {
      handler() {
        this.getChartData()
      },
      deep: true
    }
  },
}
</script>

<style scoped>

</style>
